import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CreditCard, Building2, Check, FileText, DollarSign, AlertCircle, Calendar, MapPin, Music } from "lucide-react";
import { format } from "date-fns";
import confetti from "canvas-confetti";
import { Logo } from "@/components/Logo";

interface ContractData {
  id: string;
  contract_number: string;
  client_name: string;
  client_email: string;
  event_details: any;
  packages: any;
  total_amount: number;
  payment_status: string;
  paid_at: string | null;
  payment_amount: number | null;
  payment_method: string | null;
  signed_at: string | null;
}

export default function PayContract() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const token = searchParams.get("token");
  const success = searchParams.get("success");
  const cancelled = searchParams.get("cancelled");

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [contract, setContract] = useState<ContractData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentPending, setIsPaymentPending] = useState(false);
  const confettiTriggered = useRef(false);
  const toastShown = useRef(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid payment link. Please check your email for the correct link.");
      setIsLoading(false);
      return;
    }
    fetchContract();
  }, [token]);

  // Handle success redirect from Stripe - show pending state and poll for confirmation
  useEffect(() => {
    if (success === "true" && contract && !toastShown.current) {
      toastShown.current = true;
      
      // If webhook already processed, we're done
      if (contract.payment_status === "paid") {
        return;
      }
      
      // Otherwise, show pending state and start polling
      setIsPaymentPending(true);
      
      toast({
        title: "Payment Processing... ✓",
        description: "Your payment is being confirmed. This usually takes just a moment.",
      });
      
      // Poll for payment confirmation from webhook
      pollIntervalRef.current = setInterval(async () => {
        const { data } = await supabase
          .from("inquiry_contracts")
          .select("payment_status, paid_at, payment_amount, payment_method")
          .eq("payment_token", token)
          .single();
        
        if (data?.payment_status === "paid") {
          // Webhook has processed - refresh full contract and stop polling
          clearInterval(pollIntervalRef.current!);
          setIsPaymentPending(false);
          fetchContract();
        }
      }, 2000); // Poll every 2 seconds
      
      // Stop polling after 60 seconds and show success anyway
      setTimeout(() => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          setIsPaymentPending(false);
        }
      }, 60000);
    }
    
    if (cancelled === "true" && !toastShown.current) {
      toastShown.current = true;
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled. You can try again when ready.",
        variant: "destructive",
      });
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [success, cancelled, contract?.id, contract?.payment_status]);

  // Trigger confetti on successful payment (or when returning with success=true)
  useEffect(() => {
    const shouldTriggerConfetti = 
      (contract?.payment_status === "paid" || (success === "true" && contract)) 
      && !confettiTriggered.current;
    
    if (shouldTriggerConfetti) {
      confettiTriggered.current = true;
      const count = 200;
      const defaults = { origin: { y: 0.7 } };
      function fire(particleRatio: number, opts: confetti.Options) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
        });
      }
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fire(0.1, { spread: 120, startVelocity: 45 });
    }
  }, [contract?.payment_status, success, contract]);

  const fetchContract = async () => {
    try {
      const { data, error } = await supabase
        .from("inquiry_contracts")
        .select("*")
        .eq("payment_token", token)
        .gt("payment_token_expires_at", new Date().toISOString())
        .single();

      if (error || !data) {
        setError("This payment link has expired or is invalid. Please contact us for a new link.");
        return;
      }

      if (!data.signed_at) {
        setError("This contract must be signed before payment can be processed.");
        return;
      }

      setContract(data as ContractData);
    } catch (err) {
      setError("Failed to load payment information. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async (paymentMethod: "card" | "ach") => {
    if (!contract) return;

    setIsProcessing(true);
    try {
      // Payment method selection and status update are handled by create-payment-session edge function
      const { data, error } = await supabase.functions.invoke("create-payment-session", {
        body: { 
          paymentToken: token,
          paymentMethod,
        },
      });

      if (error) throw error;
      if (!data?.url) throw new Error("Failed to create payment session");

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err: any) {
      toast({
        title: "Payment Error",
        description: err.message || "Failed to start payment. Please try again.",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <CardTitle>Payment Not Available</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")} className="w-full">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!contract) return null;

  // Show success screen if paid OR if returning from successful Stripe checkout
  const showSuccessScreen = contract.payment_status === "paid" || success === "true";
  
  if (showSuccessScreen) {
    const eventDetails = Array.isArray(contract.event_details) ? contract.event_details : [contract.event_details];
    const isPending = isPaymentPending || (success === "true" && contract.payment_status !== "paid");
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-primary/5 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <Card className="border-green-600/50 bg-gradient-to-b from-green-600/5 to-background shadow-xl">
            <CardHeader className="text-center pt-8">
              {/* Logo */}
              <div className="mx-auto mb-4">
                <Logo size="lg" />
              </div>
              
              {/* Success Icon with Animation */}
              <div className="mx-auto w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mb-4 animate-pulse">
                <Check className="h-10 w-10 text-green-600" />
              </div>
              
              <CardTitle className="text-2xl text-green-600">Booking Confirmed!</CardTitle>
              <CardDescription className="text-base">
                Thank you for choosing The Lost Strings Company
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6 pb-8">
              {/* Payment Summary */}
              <div className="bg-green-600/10 p-6 rounded-xl border border-green-600/20 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  {isPending ? "Payment Processing" : "Payment Received"}
                </p>
                <p className="text-4xl font-bold text-green-600 mb-2">
                  ${(contract.payment_amount || contract.total_amount)?.toLocaleString()}
                </p>
                {isPending ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                    <Badge variant="secondary" className="bg-green-600/20 text-green-700">
                      Confirming Payment...
                    </Badge>
                  </div>
                ) : (
                  <Badge variant="secondary" className="bg-green-600/20 text-green-700">
                    {contract.payment_method === 'us_bank_account' ? 'Bank Transfer' : 'Card'} • Paid in Full
                  </Badge>
                )}
                {contract.paid_at && !isPending && (
                  <p className="text-xs text-muted-foreground mt-3">
                    {format(new Date(contract.paid_at), "MMMM d, yyyy 'at' h:mm a")}
                  </p>
                )}
              </div>

              <Separator />
              
              {/* Contract Info */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Contract Reference</p>
                <p className="font-mono font-semibold">{contract.contract_number}</p>
              </div>

              {/* Event Details - Clean Display */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" /> Your Booked Events
                </h3>
                <div className="space-y-2">
                  {eventDetails.map((evt: any, idx: number) => (
                    <div 
                      key={idx} 
                      className="bg-card border rounded-lg p-4 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="font-semibold">{evt.occasion || `Event ${idx + 1}`}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>{evt.date}</span>
                            {evt.startTime && (
                              <>
                                <span>•</span>
                                <span>{evt.startTime}{evt.endTime ? ` – ${evt.endTime}` : ''}</span>
                              </>
                            )}
                          </div>
                          {evt.venue && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5" />
                              <span className="truncate">{evt.venue}</span>
                            </div>
                          )}
                        </div>
                        <Badge variant="outline" className="bg-green-600/10 text-green-700 border-green-600/30">
                          Confirmed
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  A confirmation email has been sent to
                </p>
                <p className="font-medium">{contract.client_email}</p>
              </div>

              <div className="text-center pt-4">
                <p className="text-sm text-muted-foreground">
                  We're excited to create an unforgettable musical experience for you! 🎵
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const eventDetails = Array.isArray(contract.event_details) ? contract.event_details : [contract.event_details];

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">The Lost Strings Company</h1>
          <p className="text-muted-foreground">Complete Your Payment</p>
        </div>

        {/* Contract Summary */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Contract #{contract.contract_number}
                </CardTitle>
                <CardDescription>For {contract.client_name}</CardDescription>
              </div>
              <Badge variant="outline" className="text-green-600 border-green-600">
                Signed ✓
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Event Summary - Clean Display */}
            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <p className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" /> Event Details
              </p>
              <div className="space-y-2">
                {eventDetails.map((evt: any, idx: number) => (
                  <div key={idx} className="bg-background/50 rounded-md p-3 border">
                    <p className="font-semibold">{evt.occasion || `Event ${idx + 1}`}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                      <span>{evt.date}</span>
                      {evt.startTime && (
                        <>
                          <span>•</span>
                          <span>{evt.startTime}{evt.endTime ? ` – ${evt.endTime}` : ''}</span>
                        </>
                      )}
                    </div>
                    {evt.venue && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{evt.venue}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Payment Amount */}
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-1">Amount Due</p>
              <p className="text-4xl font-bold text-primary">
                ${contract.total_amount?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Payment Options */}
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Choose Payment Method
            </CardTitle>
            <CardDescription>
              Select how you'd like to pay
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Card Payment */}
            <Button
              onClick={() => handlePayment("card")}
              disabled={isProcessing}
              className="w-full h-auto py-4 justify-start"
              variant="outline"
            >
              <div className="flex items-center gap-4 w-full">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold">Pay with Card</p>
                  <p className="text-sm text-muted-foreground">
                    Credit or debit card • Instant
                  </p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  2.9% + $0.30 fee
                </div>
              </div>
            </Button>

            {/* ACH Payment */}
            <Button
              onClick={() => handlePayment("ach")}
              disabled={isProcessing}
              className="w-full h-auto py-4 justify-start"
              variant="outline"
            >
              <div className="flex items-center gap-4 w-full">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold">Bank Transfer (ACH)</p>
                  <p className="text-sm text-muted-foreground">
                    Direct from bank account • 3-5 days
                  </p>
                </div>
                <div className="text-right">
                  <Badge variant="secondary" className="bg-green-500/10 text-green-700">
                    Lowest Fee
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">
                    $5 max fee
                  </p>
                </div>
              </div>
            </Button>

            {isProcessing && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="h-5 w-5 animate-spin" />
                <p className="text-sm text-muted-foreground">
                  Redirecting to secure payment...
                </p>
              </div>
            )}

            <div className="text-center pt-4">
              <p className="text-xs text-muted-foreground">
                🔒 Payments are securely processed by Stripe
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
